/**
 * Single axios instance shared by every API call.
 * - Attaches the access token if present in memory.
 * - On 401, attempts a single refresh via the cookie endpoint, then retries.
 * - Surfaces typed errors with our `{ ok, error }` envelope.
 */

import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export type ApiEnvelope<T> = { ok: true; data: T } | { ok: false; error: ApiError };

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;
const authListeners = new Set<(token: string | null) => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
  authListeners.forEach((cb) => cb(token));
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onAuthChange(cb: (token: string | null) => void): () => void {
  authListeners.add(cb);
  return () => authListeners.delete(cb);
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // refresh cookie
  timeout: 30_000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers = config.headers ?? new AxiosHeaders();
    (config.headers as AxiosHeaders).set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

interface RetryConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await axios.post<ApiEnvelope<{ accessToken: string }>>(
          `${API_BASE}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        if (res.data.ok) {
          setAccessToken(res.data.data.accessToken);
          return res.data.data.accessToken;
        }
      } catch (_) {
        // fallthrough
      }
      setAccessToken(null);
      return null;
    })().finally(() => {
      // null out so subsequent refreshes can start fresh
      setTimeout(() => { refreshPromise = null; }, 0);
    });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError<ApiEnvelope<unknown>>) => {
    const original = error.config as RetryConfig | undefined;
    if (!original || error.response?.status !== 401 || original._retried) {
      return Promise.reject(error);
    }
    // Don't try to refresh the refresh endpoint itself.
    if (original.url?.includes('/auth/refresh') || original.url?.includes('/auth/login')) {
      return Promise.reject(error);
    }
    const token = await refreshAccessToken();
    if (!token) return Promise.reject(error);
    original._retried = true;
    if (original.headers && typeof (original.headers as AxiosHeaders).set === 'function') {
      (original.headers as AxiosHeaders).set('Authorization', `Bearer ${token}`);
    } else {
      original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${token}` };
    }
    return api.request(original);
  },
);

export async function unwrap<T>(p: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const { data } = await p;
  if (!data.ok) {
    throw new Error(data.error.message || data.error.code || 'La requête a échoué');
  }
  return data.data;
}
